/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { AppThunkDispatch } from 'helpers/store'
import { setActiveViewId } from 'store/tableViews/TableViewsReducer'
import { SavedTableView } from 'types/entities/SavedTableView'
import { TableViewId } from 'types/enums/specific/TableViewId'
import { toNumericViewId } from 'utils/table/tableViewId'

export { toNumericViewId } from 'utils/table/tableViewId'

export const getActiveTableViewStorageKey = (tableId: TableViewId | string): string =>
    `active-table-view:${tableId}`

export const clearActiveTableView = (dispatch: AppThunkDispatch, tableId: TableViewId | string): void => {
    dispatch(setActiveViewId({ tableId, viewId: null }))

    try {
        window.sessionStorage.removeItem(getActiveTableViewStorageKey(tableId))
    } catch {
        // ignore storage access errors
    }
}

export const readStoredActiveTableViewId = (tableId: TableViewId | string): number | null => {
    try {
        const storedId = window.sessionStorage.getItem(getActiveTableViewStorageKey(tableId))
        return toNumericViewId(storedId)
    } catch {
        return null
    }
}

export const persistActiveTableViewId = (tableId: TableViewId | string, viewId: number): void => {
    try {
        window.sessionStorage.setItem(getActiveTableViewStorageKey(tableId), String(viewId))
    } catch {
        // ignore storage access errors
    }
}

/**
 * Restores the active view id from sessionStorage only when that view is still
 * available for the current seller/table. Otherwise clears the stale id so the
 * "Active view" banner cannot show a view that is not selected.
 */
export const hydrateActiveTableViewFromSession = (
    dispatch: AppThunkDispatch,
    tableId: TableViewId | string,
    options?: {
        views?: SavedTableView[]
        sellerId?: number | string | null
        hasLoaded?: boolean
    }
): void => {
    const storedId = readStoredActiveTableViewId(tableId)
    if (storedId === null) {
        return
    }

    const hasLoaded = options?.hasLoaded
    if (hasLoaded === false) {
        return
    }

    if (options?.views) {
        const sellerId = toNumericViewId(options.sellerId)
        const matches = options.views.some((view) => {
            if (toNumericViewId(view.id) !== storedId) {
                return false
            }
            if (view.table_id != tableId) {
                return false
            }
            if (sellerId === null) {
                return true
            }
            return toNumericViewId(view.seller_id) === sellerId
        })

        if (!matches) {
            clearActiveTableView(dispatch, tableId)
            return
        }
    }

    dispatch(setActiveViewId({ tableId, viewId: storedId }))
}
